import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Upload, X, Crop } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PhotoCropModal } from './PhotoCropModal';
import { trackPhotoUpload } from '@/hooks/useFunnelTracking';

interface PhotoUploadProps {
  photo: string | null;
  onPhotoChange: (photo: string | null) => void;
  className?: string;
  /** Custom aspect ratio (width/height) for cropping. Default: 7/9 (passport) */
  aspect?: number;
  /** Custom output pixel dimensions for cropping */
  outputSize?: { width: number; height: number };
}

export const PhotoUpload: React.FC<PhotoUploadProps> = ({
  photo,
  onPhotoChange,
  className,
  aspect,
  outputSize,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isGlobalDragging, setIsGlobalDragging] = useState(false);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragCounterRef = useRef(0);
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleFile = useCallback((file: File) => {
    const fileSizeKb = Math.round(file.size / 1024);
    const fileFormat = file.type.split('/')[1] || 'unknown';

    trackPhotoUpload('attempt', { file_size_kb: fileSizeKb, file_format: fileFormat });

    if (!file.type.startsWith('image/')) {
      trackPhotoUpload('fail', { file_size_kb: fileSizeKb, file_format: fileFormat, error_message: 'invalid_type' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      trackPhotoUpload('fail', { file_size_kb: fileSizeKb, file_format: fileFormat, error_message: 'too_large' });
      alert('File size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setOriginalImage(result);
      setShowCropModal(true);
      trackPhotoUpload('success', { file_size_kb: fileSizeKb, file_format: fileFormat });
    };
    reader.onerror = () => {
      trackPhotoUpload('fail', { file_size_kb: fileSizeKb, file_format: fileFormat, error_message: 'read_error' });
    };
    reader.readAsDataURL(file);
  }, []);

  // Reset all drag state helper
  const resetDragState = useCallback(() => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
    dragCounterRef.current = 0;
    setIsGlobalDragging(false);
    setIsDragging(false);
  }, []);

  // Global drag events for the entire document
  useEffect(() => {
    const handleGlobalDragEnter = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current++;
      if (e.dataTransfer?.types.includes('Files')) {
        setIsGlobalDragging(true);
      }
    };

    const handleGlobalDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current <= 0) {
        resetDragState();
      }
    };

    const handleGlobalDragOver = (e: DragEvent) => {
      e.preventDefault();
      
      // Clear existing timeout and set new one
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
      
      // If no dragover event within 100ms, reset (file left browser)
      dragTimeoutRef.current = setTimeout(() => {
        resetDragState();
      }, 100);
      
      if (e.dataTransfer?.types.includes('Files')) {
        setIsGlobalDragging(true);
      }
    };

    const handleGlobalDrop = (e: DragEvent) => {
      e.preventDefault();
      resetDragState();
      
      const file = e.dataTransfer?.files[0];
      if (file && file.type.startsWith('image/')) {
        handleFile(file);
      }
    };

    // Handle when drag ends (file released outside or ESC pressed)
    const handleDragEnd = () => {
      resetDragState();
    };

    document.addEventListener('dragenter', handleGlobalDragEnter);
    document.addEventListener('dragleave', handleGlobalDragLeave);
    document.addEventListener('dragover', handleGlobalDragOver);
    document.addEventListener('drop', handleGlobalDrop);
    document.addEventListener('dragend', handleDragEnd);

    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
      document.removeEventListener('dragenter', handleGlobalDragEnter);
      document.removeEventListener('dragleave', handleGlobalDragLeave);
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('drop', handleGlobalDrop);
      document.removeEventListener('dragend', handleDragEnd);
    };
  }, [handleFile, resetDragState]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resetDragState();

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile, resetDragState]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
      // Reset so the same file can be re-selected
      e.target.value = '';
    },
    [handleFile]
  );

  const handleCropComplete = useCallback(
    (croppedImage: string) => {
      onPhotoChange(croppedImage);
      setShowCropModal(false);
    },
    [onPhotoChange]
  );

  const handleRemove = useCallback(() => {
    onPhotoChange(null);
    setOriginalImage(null);
  }, [onPhotoChange]);

  const showDragState = isDragging || isGlobalDragging;

  return (
    <>
      <div ref={containerRef} className={cn('space-y-4 relative', className)}>
        {/* Global transparent orange background overlay */}
        {isGlobalDragging && !photo && (
          <div className="absolute inset-0 bg-orange-500/10 rounded-xl pointer-events-none z-10 transition-opacity duration-150" />
        )}
        
        <div
          className={cn(
            'relative border-2 border-dashed rounded-xl transition-all duration-200 z-20',
            isDragging
              ? 'border-orange-500 bg-orange-500/20 scale-[1.01] shadow-lg shadow-orange-500/25'
              : isGlobalDragging
                ? 'border-orange-400/70 bg-orange-500/5'
                : 'border-border hover:border-primary/50',
            photo ? 'p-6' : 'p-10'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif"
            onChange={handleInputChange}
            className="hidden"
            id="photo-upload-input"
          />

          {photo ? (
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <div className="relative w-20 h-26 sm:w-28 sm:h-36 rounded-lg overflow-hidden border border-border bg-muted flex-shrink-0">
                <img
                  src={photo}
                  alt="Uploaded photo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0 space-y-2 sm:space-y-3 text-center sm:text-left w-full">
                <p className="text-sm sm:text-base font-semibold text-foreground">
                  Photo uploaded successfully!
                </p>
                <div className="flex gap-2 sm:gap-3 justify-center sm:justify-start">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const imgSrc = originalImage || photo;
                      if (!imgSrc) return;
                      // If we already have a data: URL, use it directly.
                      if (imgSrc.startsWith('data:')) {
                        if (!originalImage) setOriginalImage(imgSrc);
                        setShowCropModal(true);
                        return;
                      }
                      // Otherwise the source is a remote (Cloudinary) URL — fetch and convert
                      // to a data URL to avoid canvas tainting during re-crop.
                      try {
                        const res = await fetch(imgSrc, { mode: 'cors', credentials: 'omit' });
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const blob = await res.blob();
                        const dataUrl: string = await new Promise((resolve, reject) => {
                          const fr = new FileReader();
                          fr.onloadend = () => resolve(fr.result as string);
                          fr.onerror = () => reject(fr.error);
                          fr.readAsDataURL(blob);
                        });
                        setOriginalImage(dataUrl);
                        setShowCropModal(true);
                      } catch (err) {
                        console.error('[PhotoUpload] Re-crop fetch failed:', err);
                        trackPhotoUpload('fail', { error_message: 'recrop_fetch_failed' });
                        // Fallback: try opening with the URL directly (best-effort)
                        if (!originalImage) setOriginalImage(imgSrc);
                        setShowCropModal(true);
                      }
                    }}
                    className="gap-1.5 sm:gap-2 text-xs sm:text-sm"
                  >
                    <Crop className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Re-crop
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRemove}
                    className="gap-1.5 sm:gap-2 text-xs sm:text-sm text-destructive hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <label
              htmlFor="photo-upload-input"
              className="flex flex-col items-center gap-5 cursor-pointer py-4 relative z-10"
            >
              <div
                className={cn(
                  'w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200',
                  isDragging 
                    ? 'bg-orange-500/30 scale-125 shadow-lg shadow-orange-500/30' 
                    : isGlobalDragging
                      ? 'bg-orange-500/20 scale-110'
                      : 'bg-secondary'
                )}
              >
                <Upload
                  className={cn(
                    'h-9 w-9 transition-all duration-200',
                    showDragState ? 'text-orange-500 scale-110' : 'text-muted-foreground'
                  )}
                />
              </div>
              <div className="text-center space-y-2">
                <p className={cn(
                  'text-lg font-semibold transition-colors duration-200',
                  showDragState ? 'text-orange-500' : 'text-foreground'
                )}>
                  {showDragState ? 'Drop your photo here!' : 'Drag & drop your photo'}
                </p>
                <p className="text-sm text-muted-foreground">
                  or click to browse • JPG, PNG, WebP, HEIC, AVIF • Max 10MB
                </p>
              </div>
            </label>
          )}
        </div>

        <div className={cn(
          'flex items-start gap-2 p-3 rounded-lg bg-muted/50 border transition-all duration-200',
          showDragState ? 'border-orange-400/50 bg-orange-500/5' : 'border-border'
        )}>
          <span className="text-lg">📷</span>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Use a <span className="font-medium text-foreground">passport-style photo</span> with good lighting and plain background for best results.
          </p>
        </div>
      </div>

      <PhotoCropModal
        isOpen={showCropModal}
        onClose={() => setShowCropModal(false)}
        imageSrc={originalImage}
        onCropComplete={handleCropComplete}
        aspect={aspect}
        outputSize={outputSize}
      />
    </>
  );
};
